import React, { useState, useEffect, useRef } from "react";
import { useSoundLibrary, Note } from "../soundLibrary";
import { InstrumentName } from "../types/sound";
import styles from "../styles/PianoKeyboard.module.css";

// Define key ranges for different screen sizes
const KEY_RANGES = {
    full: ["C3", "C#3", "D3", "D#3", "E3", "F3", "F#3", "G3", "G#3", "A3", "A#3", "B3",
           "C4", "C#4", "D4", "D#4", "E4", "F4", "F#4", "G4", "G#4", "A4", "A#4", "B4",
           "C5", "C#5", "D5", "D#5", "E5", "F5", "F#5", "G5"] as Note[],
    large: ["C3", "C#3", "D3", "D#3", "E3", "F3", "F#3", "G3", "G#3", "A3", "A#3", "B3",
            "C4", "C#4", "D4", "D#4", "E4", "F4", "F#4", "G4", "G#4", "A4", "A#4", "B4", "C5"] as Note[],
    medium: ["C3", "C#3", "D3", "D#3", "E3", "F3", "F#3", "G3", "G#3", "A3", "A#3", "B3",
             "C4", "C#4", "D4", "D#4", "E4", "F4", "F#4", "G4", "G#4", "A4", "A#4", "B4"] as Note[],
    small: ["C4", "C#4", "D4", "D#4", "E4", "F4", "F#4", "G4", "G#4", "A4", "A#4", "B4", "C5"] as Note[],
    tiny: ["C4", "C#4", "D4", "D#4", "E4", "F4", "F#4", "G4", "G#4", "A4"] as Note[]
};

interface PianoKeyboardProps {
    instrument: InstrumentName;
    volume?: number;
    muted?: boolean;
}

export default function PianoKeyboard({ instrument, volume = 100, muted = false }: PianoKeyboardProps) {
    const { playNote } = useSoundLibrary();
    const [activeKey, setActiveKey] = useState<Note | null>(null);
    const [keyRange, setKeyRange] = useState<Note[]>(KEY_RANGES.full);
    const containerRef = useRef<HTMLDivElement>(null);

    // Adjust key range based on container width
    useEffect(() => {
        const updateKeyRange = () => {
            const width = window.innerWidth;
            if (width >= 1200) {
                setKeyRange(KEY_RANGES.full);
            } else if (width >= 900) {
                setKeyRange(KEY_RANGES.large);
            } else if (width >= 600) {
                setKeyRange(KEY_RANGES.medium);
            } else if (width >= 400) {
                setKeyRange(KEY_RANGES.small);
            } else {
                setKeyRange(KEY_RANGES.tiny);
            }
        };

        updateKeyRange();
        window.addEventListener('resize', updateKeyRange);
        return () => window.removeEventListener('resize', updateKeyRange);
    }, []);

    const handlePlayNote = async (note: Note) => {
        try {
            setActiveKey(note);
            const activeVolume = muted ? 0 : volume * 0.01;
            await playNote(instrument, note, { velocity: activeVolume, duration: 1.5 });
            setTimeout(() => setActiveKey(null), 200);
        } catch (err) {
            console.error("Failed to play note:", err);
            setActiveKey(null);
        }
    };

    const isBlackKey = (note: Note) => note.includes('#');
    
    const whiteKeys = keyRange.filter(note => !isBlackKey(note));
    const blackKeys = keyRange.filter(note => isBlackKey(note));

    // Calculate black key position based on its white key neighbor
    const getBlackKeyPosition = (blackNote: Note): number => {
        const noteName = blackNote.slice(0, -1); // e.g., "C#"
        const octave = blackNote.slice(-1); // e.g., "3"
        
        const noteMap: Record<string, string> = {
            "C#": "C",
            "D#": "D",
            "F#": "F",
            "G#": "G",
            "A#": "A"
        };
        
        const baseNote = noteMap[noteName];
        const whiteKeyIndex = whiteKeys.findIndex(wk => 
            wk.slice(0, -1) === baseNote && wk.slice(-1) === octave
        );
        
        return whiteKeyIndex;
    };

    return (
        <div className={styles.pianoContainer} ref={containerRef}>
            <div className={styles.pianoKeyboard} data-key-count={whiteKeys.length}>
                {/* White keys */}
                {whiteKeys.map((note) => (
                    <button
                        key={note}
                        onClick={() => handlePlayNote(note)}
                        className={`${styles.whiteKey} ${activeKey === note ? styles.active : ''}`}
                    >
                        <span className={styles.noteName}>{note}</span>
                    </button>
                ))}

                {/* Black keys positioned absolutely */}
                {blackKeys.map((note) => {
                    const position = getBlackKeyPosition(note);
                    if (position === -1) return null;

                    return (
                        <button
                            key={note}
                            onClick={() => handlePlayNote(note)}
                            className={`${styles.blackKey} ${activeKey === note ? styles.active : ''}`}
                            data-position={position}
                        >
                            <span className={styles.noteName}>{note}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
